import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import axios from 'axios';
import {
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';

import EventHelpTooltip from '../../components/EventPreview/EventHelpTooltip';
import { ReactComponent as TribeLogo } from '../../assets/TribeLogo.svg';

const S = {
  page: {
    position: 'fixed',
    inset: 0,
    display: 'grid',
    gridTemplateRows: 'auto 1fr',
    height: '100dvh',
    overflow: 'hidden',
    background: '#fff',
    color: '#0f172a',
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },

  navWrap: {
    position: 'relative',
    zIndex: 100,
    background: '#fff',
    borderBottom: '1px solid #eef0f2',
  },

  navRow: {
    height: 55,
    maxWidth: 1200,
    margin: '0 auto',
    padding: '0 12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'relative',
    boxSizing: 'border-box',
  },

  navButton: {
    appearance: 'none',
    background: '#fff',
    color: '#374151',
    border: '1px solid #e5e7eb',
    borderRadius: 9999,
    padding: '6px 12px',
    fontSize: '0.8rem',
    fontWeight: 600,
    lineHeight: 1,
    height: 28,
    cursor: 'pointer',
    zIndex: 1,
  },

  logoCenter: {
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
  },

  logo: {
    height: 'auto',
    width: 72,
  },

  rightWrap: {
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },

  main: {
    minHeight: 0,
    overflow: 'hidden',
    background: '#f5f5f5',
  },

  centeredState: {
    height: '100%',
    display: 'grid',
    placeItems: 'center',
    padding: 24,
    textAlign: 'center',
    boxSizing: 'border-box',
  },

  stateTitle: {
    margin: 0,
    fontSize: 20,
    fontWeight: 800,
    color: '#0f172a',
  },

  stateText: {
    marginTop: 8,
    fontSize: 14,
    lineHeight: 1.45,
    color: '#6b7280',
  },

  iframe: {
    width: '100%',
    height: '100%',
    border: 0,
    display: 'block',
    background: '#fff',
  },
};

export default function NeutralAgendaPage() {
  const { eventId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();

  const api = process.env.REACT_APP_API_URL;

  const cleanEventId = useMemo(() => {
    return String(eventId || '').replace(
      /^neutral-/,
      ''
    );
  }, [eventId]);

  const [event, setEvent] = useState(
    state?.event || null
  );

  const [pdfUrl, setPdfUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  function goBack() {
  navigate(-1);
}

  useEffect(() => {
    let alive = true;
    let objectUrl = '';

    async function loadAgenda() {
      try {
        setLoading(true);
        setError('');

        let nextEvent = event;

        if (!nextEvent?.agendaPdfUrl) {
          const { data } = await axios.get(
            `${api}/events/${cleanEventId}`
          );

          nextEvent = data || null;

          if (alive) {
            setEvent(nextEvent);
          }
        }

        if (!nextEvent?.agendaPdfUrl) {
          throw new Error(
            'No agenda PDF is attached to this gathering.'
          );
        }

        const response = await axios.get(
          `${api}/events/${cleanEventId}/agenda-pdf`,
          {
            responseType: 'blob',
          }
        );

        if (!alive) return;

        const responseType =
          response.headers?.['content-type'] ||
          response.data?.type ||
          '';

        if (
          !responseType.includes(
            'application/pdf'
          )
        ) {
          let message =
            'The server did not return a PDF.';

          try {
            const text =
              await response.data.text();

            const parsed = JSON.parse(text);

            message =
              parsed?.message || message;
          } catch {
            // Keep fallback message.
          }

          throw new Error(message);
        }

        const pdfBlob = new Blob(
          [response.data],
          {
            type: 'application/pdf',
          }
        );

        objectUrl =
          URL.createObjectURL(pdfBlob);

        setPdfUrl(objectUrl);
      } catch (err) {
        console.error(
          '[NeutralAgendaPage]',
          err?.response?.data || err.message
        );

        if (alive) {
          setError(
            err?.response?.data?.message ||
              err.message ||
              'Could not load the agenda.'
          );
        }
      } finally {
        if (alive) {
          setLoading(false);
        }
      }
    }

    loadAgenda();

    return () => {
      alive = false;

      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [api, cleanEventId]);

  return (
    <div style={S.page}>
      <div style={S.navWrap}>
        <div style={S.navRow}>
          <button
            type="button"
onClick={goBack}
            style={S.navButton}
          >
            Back
          </button>

          <div style={S.logoCenter}>
            <TribeLogo style={S.logo} />
          </div>

          <div style={S.rightWrap}>
            <EventHelpTooltip
              title="Host agenda"
              body="Use this guide to lead the gathering. It includes the event flow, timing, meeting instructions, and host notes."
            />
          </div>
        </div>
      </div>

      <main style={S.main}>
        {loading ? (
          <div style={S.centeredState}>
            Loading agenda…
          </div>
        ) : pdfUrl ? (
          <iframe
            title={`${
              event?.name || 'Event'
            } agenda`}
            src={`${pdfUrl}#toolbar=0&navpanes=0&view=FitH`}
            style={S.iframe}
          />
        ) : (
          <div style={S.centeredState}>
            <div>
              <h1 style={S.stateTitle}>
                Agenda unavailable
              </h1>

              <div style={S.stateText}>
                {error ||
                  'This gathering does not have a host agenda yet.'}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}